import React from 'react';
import { ClerkProvider, SignedIn, SignedOut, SignIn, SignUp, useUser } from '@clerk/clerk-react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import DashboardNew from './pages/DashboardNew';
import PreviewWorkDesk from './pages/PreviewWorkDesk';
import PreviewClientPortal from './pages/PreviewClientPortal';
import PreviewIndex from './pages/PreviewIndex';
import PreviewAttorneyDashboard from './pages/PreviewAttorneyDashboard';
import PreviewVerificationLayer from './pages/PreviewVerificationLayer';
import PreviewCasePath from './pages/PreviewCasePath';
import PreviewDataSpecialist from './pages/PreviewDataSpecialist';
import PreviewWarRoom from './pages/PreviewWarRoom';
import PreviewDocumentHub from './pages/PreviewDocumentHub';
import PreviewAdminOrchestrator from './pages/PreviewAdminOrchestrator';
import PreviewExternalAccountant from './pages/PreviewExternalAccountant';
import NewClaim from './pages/NewClaim';
import Wizard from './pages/Wizard';
import WizardV2Page from './pages/WizardV2';
import ReportDetail from './pages/ReportDetail';
import NarratorPage from './pages/NarratorPage';
import DemoMode from './pages/DemoMode';
import DemoNarratorPage from './pages/DemoNarratorPage';
import DemoWizard from './pages/DemoWizard';
import DemoReportDetail from './pages/DemoReportDetail';
import CSVFeaturesDemo from './pages/CSVFeaturesDemo';
import NotFound from './pages/NotFound';
import BulkUpload from './pages/BulkUpload';
import CompareReports from './pages/CompareReports';
// Accountant portal routes are declared in a dedicated module so the JWT
// auth boundary stays visible (Phase 00.2 of the attestation plan).
import { externalAccountantRoutes } from './router/externalAccountantRoutes';
import CleanupValidation from './pages/CleanupValidation';
import PublicShareView from './pages/PublicShareView';
import SettingsPage from './pages/SettingsPage';
import MyReportsPage from './pages/MyReportsPage';
import AdminFirmsLanding from './pages/admin/AdminFirmsLanding';
import AdminFirmCases from './pages/admin/AdminFirmCases';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminGoldenCases from './pages/admin/AdminGoldenCases';
// Admin System-group tools (spec §9) — promoted out of the Settings 2nd sidebar
// into the rail. Reuse the existing panels; each rendered standalone in AppLayout.
import AppLayout from './components/AppLayout';
import WaitlistAdmin from './components/admin/WaitlistAdmin';
import FeatureFlagsPanel from './components/admin/FeatureFlagsPanel';
import DataHealthWidget from './components/dashboard/DataHealthWidget';
import ErrorLogViewer from './components/ErrorLogViewer';
import TeamManagement from './components/TeamManagement';

const AdminToolPage = ({ children }: { children: React.ReactNode }) => (
  <AppLayout><div className="p-6 max-w-5xl mx-auto">{children}</div></AppLayout>
);
import AdminActivityLog from './pages/AdminActivityLog';
import AnalyticsPage from './pages/AnalyticsPage';
import VendorIntelligencePage from './pages/VendorIntelligencePage';
import GameTheoryPage from './pages/GameTheoryPage';
import SettlementIntelligencePage from './pages/SettlementIntelligencePage';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import WaitlistPage from './pages/WaitlistPage';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import ForCarriersPage from './pages/ForCarriersPage';
import CarrierSignupPage from './pages/CarrierSignupPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
// DocsGuidePage re-homed to client portal as ClientPrepGuideTab (Phase 5.2)
// UnifiedUploadPage import removed: /upload now redirects to /wizard-v2 (see route below)
import DataReviewPage from './pages/DataReviewPage';
import ClosedCasesPage from './pages/ClosedCasesPage';
import ClientUploadPortal from './pages/ClientUploadPortal';
import ClientClarificationPortal from './pages/ClientClarificationPortal';
import UnifiedClientPortal from './pages/UnifiedClientPortal';
import GuestCaseReview from './pages/GuestCaseReview';
import ClientPortalLayout from './layouts/ClientPortalLayout';
import { UploadPortalRouter, ClarificationPortalRouter } from './components/routing/ClientPortalRouter';
import ClientStatusTab from './pages/portal/ClientStatusTab';
import ClientPrepGuideTab from './pages/portal/ClientPrepGuideTab';
import { DataSpecialistQueueRouter, TransactionReviewRouter, IssueTrackerRouter } from './components/routing/DataSpecialistRouter';
import UnifiedWorkQueue from './pages/UnifiedWorkQueue';
// FL-5 5.2 — components/work-queue UnifiedWorkQueue (was UnifiedWorkQueueV2) retired;
// /data-specialist/work-queue now redirects to /workdesk/data-specialist. File staged for deletion.
import BatchProcessingPage from './pages/BatchProcessingPage';
import { AccountantAuthProvider } from './contexts/AccountantAuthContext';
import { AttorneyAuthProvider, ProtectedAttorneyRoute } from './contexts/AttorneyAuthContext';
import { setAuthTokenGetter } from './utils/api';
import { useAuth } from '@clerk/clerk-react';
import AttorneyWorkDesk from './pages/attorney/AttorneyWorkDesk';
import AttorneyLetterheadPage from './pages/attorney/AttorneyLetterheadPage';
import AttorneyWarRoomPage from './pages/AttorneyWarRoomPage';
import AttorneyOverlayPage from './pages/AttorneyOverlayPage';
import AttorneyCaseDetail from './pages/AttorneyCaseDetail';
import AttorneyPackageView from './pages/AttorneyPackageView';
import DemandLetterGenerator from './pages/DemandLetterGenerator';
import ProofOfLossGenerator from './pages/ProofOfLossGenerator';
import EUOPrepMemoGenerator from './pages/EUOPrepMemoGenerator';
import RoRResponseGenerator from './pages/RoRResponseGenerator';
import RoRAnalysisMemoGenerator from './pages/RoRAnalysisMemoGenerator';
import CaseDocumentCenter from './pages/CaseDocumentCenter';
import BITimelinePage from './pages/BITimelinePage';
// PolicyDNAPage removed - policy coverage now integrated into AttorneyCaseDetail
import IssueDetail from './pages/IssueDetail';
import AdjusterAnalyzerView from './pages/AdjusterAnalyzerView';
import OrchestratorDashboard from './pages/OrchestratorDashboard';
// FleetHud: admin-only fleet observability page — consumed at /fleet route below
import FleetHud from './pages/FleetHud';
import StaffRoadmapPage from './pages/StaffRoadmapPage';
import PatternAdminPage from './pages/PatternAdminPage';
import WarRoom from './pages/WarRoom';
import MediationBoard from './pages/MediationBoard';
import MediationWall from './pages/MediationWall';  // Phase 7 M3 — external neutral workspace
// Phase 6 — New pages (alongside existing, not replacing yet)
const CaseStatusPage = React.lazy(() => import('./pages/CaseStatusPage'));
const NewCaseWizard = React.lazy(() => import('./pages/NewCaseWizard'));
// GAP-P3D-LIVE-PAGE — live Case Path route, real backend data (frontend/src/pages/CasePathLive.tsx)
const CasePathLive = React.lazy(() => import('./pages/CasePathLive'));
// Item Matcher (adjuster/contractor) — live reconciliation in the four-state matcher view
const PropertyMatcherPage = React.lazy(() => import('./pages/PropertyMatcherPage'));
const PropertyMatchWorkspace = React.lazy(() => import('./pages/PropertyMatchWorkspace'));
const ReconcilePage = React.lazy(() => import('./pages/ReconcilePage'));
const ClaimLedger = React.lazy(() => import('./pages/ClaimLedger'));
import StaffDashboard from './pages/StaffDashboard';
// FL-5 5.2 — DataSpecialistDashboard retired; /dashboard/data-specialist now
// redirects to /workdesk/data-specialist. File staged for deletion.
import AccountantDashboardRole from './pages/dashboards/AccountantDashboard';
import StaffWorkbenchPage from './pages/dashboards/StaffWorkbenchPage';
import StaffExportCenterPage from './pages/dashboards/StaffExportCenterPage';
import InternalAccountantWorkDesk from './pages/workdesk/internal-accountant/WorkDesk';
// ExternalAccountantWorkDesk is mounted in the JWT tree (router/externalAccountantRoutes.tsx),
// NOT here — external CPAs auth via AccountantAuthContext (no Clerk session). See WS2-P0.
import AccountantEngineReviewLanding from './pages/AccountantEngineReviewLanding';
import AccountantAnomalyReviewInternal from './pages/AccountantAnomalyReviewInternal';
import AccountantEngineReviewDetail from './pages/AccountantEngineReviewDetail';
import AccountantMethodologyView from './pages/AccountantMethodologyView';
import AccountantAnalysisTools from './pages/AccountantAnalysisTools';
import ToolsHub from './pages/ToolsHub';
import CopilotPanelPage from './pages/CopilotPanel.page';
import EngineAuditPage from './pages/EngineAuditPage';
import GeneratorsPage from './pages/generators/GeneratorsPage';
import { AnalysisPage } from './pages/analysis';
import AnalysisTab from './pages/reports/AnalysisTab';
import { BrandingProvider } from './contexts/BrandingContext';
import { GlobalErrorBoundary } from './components/GlobalErrorBoundary';
import { TestAuthProvider, isTestMode, TestSignedIn, TestSignedOut, useClerk } from './contexts/TestAuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { useLocation, useParams } from 'react-router-dom';
import { RoleRoute } from './components/auth/RoleRoute';
import { SeatRouteGuard } from './components/auth/SeatRouteGuard';
import { useWorkspaceRole } from './hooks/useWorkspaceRole';
import { isFeatureEnabled } from './config/featureFlags';
import { WorkDesk } from './components/workdesk';
import { CaseWorkspaceShell } from './components/workspace';
import PublicAdjusterWorkdesk from './pages/PublicAdjusterWorkdesk';
import CarrierPortal from './pages/CarrierPortal';

const CLERK_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || 'pk_test_bWFnbmV0aWMtc2hlZXAtNDUuY2xlcmsuYWNjb3VudHMuZGV2JA';

// Redirect component that preserves query parameters
function WizardRedirect() {
  const location = useLocation();
  // Preserve query params when redirecting from /wizard to /wizard-v2
  const newPath = `/wizard-v2${location.search}`;
  return <Navigate to={newPath} replace />;
}

// Attorney case index redirect: the bare /attorney/case/:caseId enters the 7-stage
// workspace at its first stage (Intake) when CASE_WORKSPACE is on. Replaces the legacy
// AttorneyCaseDetail sidebar page as the case-view entry point.
function AttorneyCaseIndexRedirect() {
  const { caseId } = useParams<{ caseId: string }>();
  return <Navigate to={`/attorney/case/${caseId}/intake`} replace />;
}

function AdjusterCaseIndexRedirect() {
  const { caseId } = useParams<{ caseId: string }>();
  return <Navigate to={`/adjuster/case/${caseId}/analysis`} replace />;
}

// Legacy /adjuster/:reportId rendered the DEPRECATED AdjusterAnalyzerView (AppLayout
// + left sidebar). When CASE_WORKSPACE is on (prod), send it to the current no-sidebar
// adjuster shell (ANALYSIS stage); flag-off keeps the legacy page (reversible), mirroring
// the attorney /case index behavior. No inbound links point here — URL-typing only.
function LegacyAdjusterRedirect() {
  const { reportId } = useParams<{ reportId: string }>();
  return <Navigate to={`/adjuster/case/${reportId}/analysis`} replace />;
}

// Legacy settlement redirect: /case/:id/settlement was the deprecated sidebar
// SettlementPage (its per-tool API adapters 404'd/422'd on the current data model).
// The live settlement experience is the attorney workspace's Settlement stage
// (AttorneySettlementContent → SettlementIntelligenceDashboard), verified healthy
// against the deployed backend. Send anyone landing on the old path there so no
// one reaches the broken page (command-palette entry, breadcrumbs, old bookmarks).
function SettlementStageRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/attorney/case/${id}/settlement`} replace />;
}

// The live War Room is /deliberation/{reportId}, but /war-room/{reportId} and
// /preview/war-room/{reportId} are the natural guesses — both dead-ended (404),
// stranding users on the static /preview/war-room design mock with stale numbers.
function WarRoomRedirect() {
  const { reportId } = useParams<{ reportId: string }>();
  return <Navigate to={`/deliberation/${reportId}`} replace />;
}

// Redirect the legacy advanced-tools route into the workspace Settlement stage
// (Bad Faith / §542 / Case Law live under Settlement → Leverage).
function AdvancedToolsRedirect() {
  const { caseId } = useParams<{ caseId: string }>();
  return <Navigate to={`/attorney/case/${caseId}/settlement`} replace />;
}

// Phase 3.1 — Attorney WorkDesk consolidation redirects
function AttorneyDashboardRedirect() {
  return <Navigate to="/attorney/workdesk" replace />;
}

function AttorneyPortfolioRedirect() {
  return <Navigate to="/attorney/workdesk" replace />;
}

function AttorneyQueueRedirect() {
  return <Navigate to="/attorney/workdesk" replace />;
}

// FL-5 5.2 — Data Specialist surface consolidation. The DS landing is the
// role-parameterized WorkDesk at /workdesk/data-specialist (the surface with the
// live queues; wired 2026-07-31). The frozen UnifiedWorkQueue duplicate
// (/data-specialist/work-queue) and the legacy DataSpecialistDashboard
// (/dashboard/data-specialist) redirect into it. NOTE: /queue is deliberately NOT
// consolidated here — it is the SHARED admin/analyst/DS detail queue the WorkDesk
// itself drills into (see toolsManifest 'work-queue' roles + WorkDesk.tsx metric
// deep-link), so redirecting it would strand two other roles.
function DataSpecialistWorkQueueRedirect() {
  return <Navigate to="/workdesk/data-specialist" replace />;
}

function DataSpecialistDashboardRedirect() {
  return <Navigate to="/workdesk/data-specialist" replace />;
}

// PR-2 (2026-07-12) hard cutover: /attorney/login (and forgot/reset-password)
// no longer render the AttorneyLogin JWT form — that page and its mint flow
// are LEGACY-JWT, kept alive (not deleted) only for the bake period. Every
// visitor is bounced to the app's own Clerk sign-in page (/sign-in), which
// lands back on the WorkDesk after auth. No transition period: no real
// attorney ever successfully used the JWT portal (its portfolio query was
// structurally broken until 2026-07-12).
function AttorneyLoginRedirect() {
  return (
    <>
      <SignedIn><Navigate to="/attorney/workdesk" replace /></SignedIn>
      <SignedOut><Navigate to="/sign-in?redirect_url=/attorney/workdesk" replace /></SignedOut>
    </>
  );
}

// /sign-out and /logout: sign the user out via the same Clerk mechanism the
// app shell uses, then land on /sign-in (fallback runs even if signOut rejects).
export function SignOutRoute() {
  const { signOut } = useClerk();
  React.useEffect(() => {
    void Promise.resolve(signOut({ redirectUrl: '/sign-in' })).finally(() => {
      window.location.assign('/sign-in');
    });
  }, [signOut]);
  return null;
}

// Component that shows HomePage for non-authenticated users, redirects to dashboard for authenticated
function ConditionalHome() {
  const role = useWorkspaceRole();

  // Role-based landing (Phase 3.1) — gated by ROLE_LANDING flag.
  if (isFeatureEnabled('ROLE_LANDING') && role) {
    // Role → landing route mapping. PARTIAL BY DESIGN (owner-directed IA
    // activation, 2026-10-04): this map lists ONLY the roles whose current
    // landing is wrong — i.e. roles that today get dumped on the generic staff
    // Claims Dashboard (DashboardNew) instead of the role home they already
    // have. Every role NOT listed deliberately falls through to the legacy
    // /dashboard block below:
    //   • admin / analyst  → owner + unspecified internal staff. DashboardNew
    //     (the claims dashboard) is their established working surface; do not
    //     reroute it out from under them.
    //   • accountant_external → its WorkDesk lives in the JWT router tree
    //     (externalAccountantRoutes.tsx), NOT this Clerk tree; never Navigate a
    //     Clerk session to /workdesk/accountant-external (WS2-P0).
    //   • client → reduced portal lens (Phase 5), no WorkDesk.
    // attorney lands on /attorney/workdesk — the rich AttorneyWorkDesk portfolio
    // hub, the SAME surface /attorney, /attorney/login and /attorney/dashboard
    // already redirect signed-in attorneys to — NOT the thinner generic
    // /workdesk/attorney (WorkDesk role="attorney").
    // SSOT: these landing routes MUST match `dashboardRoute` in
    // config/seats.config.ts (homepage "Open the workspace" deep-links) and
    // docs/architecture/SEAT_DASHBOARD_MAP.md. Keep all three in sync.
    const roleLandingRoutes: Record<string, string> = {
      attorney: '/attorney/workdesk',
      attorney_internal: '/attorney/workdesk',
      accountant_internal: '/workdesk/accountant-internal',
      data_specialist: '/workdesk/data-specialist',
      // Phase 7 neutral: the mediator is NOT a firm seat (separate org_type). seats.config.ts
      // NEUTRAL has workspaceRole 'mediator' but dashboardRoute null (no homepage deep-link);
      // the stripped wall is its only landing. See SEAT_DASHBOARD_MAP.md.
      mediator: '/mediation-wall',
      client: '/client',
      public_adjuster: '/public-adjuster',
      carrier_adjuster: '/carrier',
    };

    const landingRoute = roleLandingRoutes[role];
    if (landingRoute) {
      return <Navigate to={landingRoute} replace />;
    }
  }

  // Legacy behavior (when ROLE_LANDING is false or no role detected)
  if (isTestMode) {
    // In test mode, user is always "logged in", go to dashboard
    return <Navigate to="/dashboard" replace />;
  }
  // In production, show homepage for signed out, dashboard for signed in
  return (
    <>
      <SignedIn><Navigate to="/dashboard" replace /></SignedIn>
      <SignedOut><HomePage /></SignedOut>
    </>
  );
}

// Wrapper components that use test mode or Clerk based on environment
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  if (isTestMode) {
    return <>{children}</>;
  }
  return (
    <>
      <SignedIn><SeatRouteGuard>{children}</SeatRouteGuard></SignedIn>
      <SignedOut><Navigate to="/sign-in" replace /></SignedOut>
    </>
  );
}

// Component to register the auth token getter for the API utility
// IMPORTANT: Blocks rendering until Clerk is loaded to prevent race condition
// where useClaimId fires POST /api/v1/claims before auth token getter is registered
function AuthTokenRegistrar({ children }: { children: React.ReactNode }) {
  const { getToken, isLoaded } = useAuth();
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    if (isLoaded) {
      // Register the Clerk getToken function with the API utility
      setAuthTokenGetter(getToken);
      console.log('Clerk auth loaded, token getter registered');
      setReady(true);
    }
  }, [isLoaded, getToken]);

  if (!ready) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--color-brand)' }} />
      </div>
    );
  }

  return <>{children}</>;
}

function AppContent() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public marketing pages - no auth required */}
        <Route path="/home" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/for-carriers" element={<ForCarriersPage />} />
        <Route path="/for-carriers/start" element={<CarrierSignupPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />

        {/* Public Share View - No auth required */}
        <Route path="/share/:shareToken" element={<PublicShareView />} />
        
        {/* Public demo routes */}
        <Route path="/demo" element={<DemoMode />} />
        <Route path="/demo-narrator" element={<DemoNarratorPage />} />
        <Route path="/demo-wizard" element={<DemoWizard />} />
        <Route path="/demo-report/:id" element={<DemoReportDetail />} />
        <Route path="/csv-features" element={<CSVFeaturesDemo />} />

        {/* Public Upload Portal - Feature flag aware (redirects to unified when enabled) */}
        <Route path="/upload/:reportId" element={<UploadPortalRouter />} />
        
        {/* Public Clarification Portal - Feature flag aware (redirects to unified when enabled) */}
        <Route path="/clarify/:token" element={<ClarificationPortalRouter />} />
        
        {/* Unified Client Portal (Documents + Clarifications) - No auth required */}
        <Route path="/portal/:token" element={<UnifiedClientPortal />} />
        <Route path="/guest/:token" element={<GuestCaseReview />} />

        {/* Phase 5.2 — Client Portal Status and Prep-Guide tabs */}
        <Route path="/portal/:token/status" element={<ClientPortalLayout activeTab="overview"><ClientStatusTab /></ClientPortalLayout>} />
        <Route path="/portal/:token/prep-guide" element={<ClientPortalLayout activeTab="overview"><ClientPrepGuideTab /></ClientPortalLayout>} />

        {/* Phase 5.1 — Unified Client Portal routes (/client with Intake-stage tabs) */}
        <Route path="/client" element={<ClientPortalLayout activeTab="overview"><UnifiedClientPortal /></ClientPortalLayout>} />
        <Route path="/client/upload" element={<ClientPortalLayout activeTab="upload"><UnifiedClientPortal /></ClientPortalLayout>} />
        <Route path="/client/clarify" element={<ClientPortalLayout activeTab="clarify"><UnifiedClientPortal /></ClientPortalLayout>} />
        <Route path="/client/status" element={<ClientPortalLayout activeTab="overview"><ClientStatusTab /></ClientPortalLayout>} />
        <Route path="/client/prep-guide" element={<ClientPortalLayout activeTab="overview"><ClientPrepGuideTab /></ClientPortalLayout>} />
        
        {/* Waitlist page - always accessible */}
        <Route path="/waitlist" element={<WaitlistPage />} />
        
        {/* Sign in/up routes - skip in test mode */}
        {!isTestMode && (
          <>
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/sign-up" element={<SignUpPage />} />
          </>
        )}
        
        {/* In test mode, redirect sign-in to dashboard */}
        {isTestMode && (
          <>
            <Route path="/sign-in" element={<Navigate to="/" replace />} />
            <Route path="/sign-up" element={<Navigate to="/" replace />} />
          </>
        )}
        
        {/* Root route - shows homepage for non-auth, dashboard for auth */}
        <Route path="/" element={<ConditionalHome />} />
        
        {/* Protected routes */}
        <Route path="/tools-hub" element={<ProtectedRoute><ToolsHub /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardNew /></ProtectedRoute>} />
        {/* Redesign preview surface — pixel-faithful Daylight Work Desk, real data. Does not touch /dashboard or flags. */}
        <Route path="/preview" element={<ProtectedRoute><PreviewIndex /></ProtectedRoute>} />
        <Route path="/preview/workdesk" element={<ProtectedRoute><PreviewWorkDesk /></ProtectedRoute>} />
        <Route path="/preview/client-portal" element={<ProtectedRoute><PreviewClientPortal /></ProtectedRoute>} />
        <Route path="/preview/attorney-dashboard" element={<ProtectedRoute><PreviewAttorneyDashboard /></ProtectedRoute>} />
        <Route path="/preview/verification-layer" element={<ProtectedRoute><PreviewVerificationLayer /></ProtectedRoute>} />
        <Route path="/preview/case-path" element={<ProtectedRoute><PreviewCasePath /></ProtectedRoute>} />
        {/* GAP-P3D-LIVE-PAGE — live Case Path route, real backend data (no fixtures) */}
        <Route path="/case/:reportId/path" element={<ProtectedRoute><React.Suspense fallback={<div className="p-6 animate-pulse">Loading...</div>}><CasePathLive /></React.Suspense></ProtectedRoute>} />
        <Route path="/matcher/:reportId" element={<ProtectedRoute><React.Suspense fallback={<div className="p-6 animate-pulse">Loading...</div>}><PropertyMatchWorkspace /></React.Suspense></ProtectedRoute>} />
        <Route path="/reconcile/:reportId" element={<ProtectedRoute><React.Suspense fallback={<div className="p-6 animate-pulse">Loading...</div>}><ReconcilePage /></React.Suspense></ProtectedRoute>} />
        <Route path="/preview/data-specialist" element={<ProtectedRoute><PreviewDataSpecialist /></ProtectedRoute>} />
        <Route path="/preview/war-room" element={<ProtectedRoute><PreviewWarRoom /></ProtectedRoute>} />
        <Route path="/preview/war-room/:reportId" element={<ProtectedRoute><WarRoomRedirect /></ProtectedRoute>} />
        <Route path="/war-room/:reportId" element={<ProtectedRoute><WarRoomRedirect /></ProtectedRoute>} />
        <Route path="/preview/document-hub" element={<ProtectedRoute><PreviewDocumentHub /></ProtectedRoute>} />
        <Route path="/preview/admin-orchestrator" element={<ProtectedRoute><PreviewAdminOrchestrator /></ProtectedRoute>} />
        <Route path="/preview/external-accountant" element={<ProtectedRoute><PreviewExternalAccountant /></ProtectedRoute>} />
        <Route path="/new-claim" element={<ProtectedRoute><NewClaim /></ProtectedRoute>} />
        {/* /upload (standalone quick-parse) retired: it had no claim in scope and silently
            discarded reviewed data on confirm. Redirect to the wizard, which persists + calculates. */}
        <Route path="/upload" element={<Navigate to="/wizard-v2" replace />} />
        {/* Old wizard redirects to new wizard-v2 (preserves query params) */}
        <Route path="/wizard" element={<WizardRedirect />} />
        <Route path="/wizard-v2" element={<ProtectedRoute><WizardV2Page /></ProtectedRoute>} />
        <Route path="/report/:id" element={<ProtectedRoute><ReportDetail /></ProtectedRoute>} />
        <Route path="/compare" element={<ProtectedRoute><CompareReports /></ProtectedRoute>} />
        <Route path="/narrator/:id" element={<ProtectedRoute><NarratorPage /></ProtectedRoute>} />
        <Route path="/bulk-upload" element={<ProtectedRoute><BulkUpload /></ProtectedRoute>} />
        {/* Data Cleanup / Transaction Review routes */}
        <Route path="/cleanup-validation" element={<ProtectedRoute><CleanupValidation /></ProtectedRoute>} />
        <Route path="/cleanup/validation/:jobId" element={<ProtectedRoute><CleanupValidation /></ProtectedRoute>} />
        <Route path="/cleanup" element={<Navigate to="/wizard-v2" replace />} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/my-reports" element={<ProtectedRoute><MyReportsPage /></ProtectedRoute>} />
        <Route path="/settings/activity" element={<RoleRoute requiredRoles={['admin']} redirectTo="/settings"><AdminActivityLog /></RoleRoute>} />
        <Route path="/settings/patterns" element={<RoleRoute requiredRoles={['admin']} redirectTo="/settings"><PatternAdminPage /></RoleRoute>} />
        {/* Firm-first admin shell (admin dashboard spec SS1/SS2) */}
        {/* Phase 7 mediation wall — the external NEUTRAL's only surface (mediator org). */}
        <Route path="/mediation-wall" element={<RoleRoute requiredRoles={['mediator']} redirectTo="/"><MediationWall /></RoleRoute>} />
        <Route path="/mediation-wall/:mediationId" element={<RoleRoute requiredRoles={['mediator']} redirectTo="/"><MediationWall /></RoleRoute>} />
        {/* PR5 — Public Adjuster seat landing + per-case view */}
        <Route path="/public-adjuster" element={<RoleRoute requiredRoles={['public_adjuster']} redirectTo="/"><PublicAdjusterWorkdesk /></RoleRoute>} />
        <Route path="/public-adjuster/case/:caseId" element={<RoleRoute requiredRoles={['public_adjuster']} redirectTo="/"><PublicAdjusterWorkdesk /></RoleRoute>} />
        {/* PR5 — Carrier Adjuster seat landing + per-delivery view */}
        <Route path="/carrier" element={<RoleRoute requiredRoles={['carrier_adjuster']} redirectTo="/"><CarrierPortal /></RoleRoute>} />
        <Route path="/carrier/delivery/:deliveryId" element={<RoleRoute requiredRoles={['carrier_adjuster']} redirectTo="/"><CarrierPortal /></RoleRoute>} />
        <Route path="/admin/firms" element={<RoleRoute requiredRoles={['admin']} redirectTo="/"><AdminFirmsLanding /></RoleRoute>} />
        <Route path="/admin/firms/:id" element={<RoleRoute requiredRoles={['admin']} redirectTo="/"><AdminFirmCases /></RoleRoute>} />
        <Route path="/admin/users" element={<RoleRoute requiredRoles={['admin']} redirectTo="/"><AdminUsersPage /></RoleRoute>} />
        <Route path="/admin/golden-cases" element={<RoleRoute requiredRoles={['admin']} redirectTo="/"><AdminGoldenCases /></RoleRoute>} />
        {/* System group (spec §9) — admin tools promoted out of the Settings 2nd sidebar */}
        <Route path="/admin/leads" element={<RoleRoute requiredRoles={['admin']} redirectTo="/"><AdminToolPage><WaitlistAdmin /></AdminToolPage></RoleRoute>} />
        <Route path="/admin/team" element={<RoleRoute requiredRoles={['admin']} redirectTo="/"><AdminToolPage><TeamManagement /></AdminToolPage></RoleRoute>} />
        <Route path="/admin/feature-flags" element={<RoleRoute requiredRoles={['admin']} redirectTo="/"><AdminToolPage><FeatureFlagsPanel /></AdminToolPage></RoleRoute>} />
        <Route path="/admin/data-health" element={<RoleRoute requiredRoles={['admin']} redirectTo="/"><AdminToolPage><DataHealthWidget /></AdminToolPage></RoleRoute>} />
        <Route path="/admin/error-logs" element={<RoleRoute requiredRoles={['admin']} redirectTo="/"><AdminToolPage><ErrorLogViewer /></AdminToolPage></RoleRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
        <Route path="/vendor-intelligence" element={<ProtectedRoute><VendorIntelligencePage /></ProtectedRoute>} />
        {/* Privilege firewall (2026-08-22): standalone Game Theory / Settlement
            Intelligence pages are restricted to {attorney, admin, owner} —
            accountant/data_specialist/analyst excluded. Frontend layer only
            (UX/redirect); see docs/plans/2026-08-22-privilege-firewall-plan.md.
            'owner' has no distinct WorkspaceRole literal — useWorkspaceRole
            normalizes it to 'admin' (hooks/useWorkspaceRole.ts), so ['attorney','admin']
            already covers the full {attorney, admin, owner} allow-list. */}
        <Route path="/game-theory" element={<RoleRoute requiredRoles={['attorney', 'admin']} redirectTo="/"><GameTheoryPage /></RoleRoute>} />
        <Route path="/settlement-intelligence" element={<RoleRoute requiredRoles={['attorney', 'admin']} redirectTo="/"><SettlementIntelligencePage /></RoleRoute>} />
        {/* /docs-guide re-homed to client portal (Phase 5.2) — redirect preserves old bookmarks */}
        <Route path="/docs-guide" element={<Navigate to="/client/prep-guide" replace />} />
        <Route path="/review" element={<ProtectedRoute><DataReviewPage /></ProtectedRoute>} />
        <Route path="/closed-cases" element={<ProtectedRoute><ClosedCasesPage /></ProtectedRoute>} />
        <Route path="/review-queue" element={<ProtectedRoute><TransactionReviewRouter /></ProtectedRoute>} />
        <Route path="/batch-processing" element={<ProtectedRoute><BatchProcessingPage /></ProtectedRoute>} />
        
        {/* Issue Tracker Routes (Phase 3.5a) - Feature flag aware */}
        <Route path="/issues" element={<ProtectedRoute><IssueTrackerRouter /></ProtectedRoute>} />
        <Route path="/issues/:issueId" element={<ProtectedRoute><IssueDetail /></ProtectedRoute>} />
        
        {/* Orchestrator Dashboard (Phase 4) — Admin only */}
        <Route path="/orchestrator" element={<RoleRoute requiredRoles={['admin']} redirectTo="/"><OrchestratorDashboard /></RoleRoute>} />
        <Route path="/fleet" element={<RoleRoute requiredRoles={['admin']} redirectTo="/"><FleetHud /></RoleRoute>} />

        {/* AI Co-pilot Panel (Phase 1.2) */}
        <Route path="/copilot" element={<ProtectedRoute><CopilotPanelPage /></ProtectedRoute>} />

        {/* Engine Audit Panel (Phase 1.3) */}
        <Route path="/engine-audit/:reportId" element={<ProtectedRoute><EngineAuditPage /></ProtectedRoute>} />

        {/* Agent War Room — Deliberation View */}
        <Route path="/deliberation/:reportId" element={<ProtectedRoute><WarRoom /></ProtectedRoute>} />

        {/* Mediation Board — standalone NEUTRAL surface (third audience: mediator + both
            parties). ProtectedRoute = same auth as case pages; NOT the attorney shell. */}
        <Route path="/mediation/:reportId" element={<ProtectedRoute><MediationBoard /></ProtectedRoute>} />

        {/* Adjuster: the current surface is the no-sidebar CaseWorkspaceShell
            (role="adjuster", /adjuster/case/:id/:stage). The legacy AdjusterAnalyzerView
            (AppLayout+sidebar) is deprecated — redirect to the shell when CASE_WORKSPACE
            is on (prod), fall back to the legacy page when off (reversible). */}
        <Route path="/adjuster/:reportId" element={<ProtectedRoute>{isFeatureEnabled('CASE_WORKSPACE') ? <LegacyAdjusterRedirect /> : <AdjusterAnalyzerView />}</ProtectedRoute>} />
        
        {/* Case Strengthening Roadmap (Staff View) */}
        <Route path="/roadmap/:reportId" element={<ProtectedRoute><StaffRoadmapPage /></ProtectedRoute>} />
        
        {/* Queue-Based Dashboards (Now-Line) - Feature flag aware */}
        <Route path="/queue" element={<ProtectedRoute><UnifiedWorkQueue /></ProtectedRoute>} />
        <Route path="/queue/data-specialist" element={<ProtectedRoute><DataSpecialistQueueRouter /></ProtectedRoute>} />
        {/* FL-5 5.2 — consolidated into /workdesk/data-specialist (frozen UnifiedWorkQueue retired) */}
        <Route path="/data-specialist/work-queue" element={<ProtectedRoute><DataSpecialistWorkQueueRedirect /></ProtectedRoute>} />
        <Route path="/queue/accountant" element={<Navigate to="/dashboard/accountant?tab=queue" replace />} />
        <Route path="/staff" element={<ProtectedRoute><StaffDashboard /></ProtectedRoute>} />
        {/* Phase 6 — New routes (alongside existing) */}
        <Route path="/case/:claimId" element={<ProtectedRoute><React.Suspense fallback={<div className="p-6 animate-pulse">Loading...</div>}><CaseStatusPage /></React.Suspense></ProtectedRoute>} />
        <Route path="/case/:claimId/documents" element={<ProtectedRoute><CaseDocumentCenter /></ProtectedRoute>} />
        <Route path="/case/:claimId/ledger" element={<ProtectedRoute><React.Suspense fallback={<div className="p-6 animate-pulse">Loading...</div>}><ClaimLedger /></React.Suspense></ProtectedRoute>} />

        {/* Phase 4 — Case Workspace routes (feature-flag gated) */}
        {isFeatureEnabled('CASE_WORKSPACE') && (
          <>
            {/* WS-OWNER-A: the owner/accountant case route renders the 8-stage accountant
                workspace (role="accountant"), not the 5-tab default. The 8-stage workspace
                is the real accountant surface; it was previously only reachable via the
                external-CPA JWT portal (/accountant/reports/:id/:stage). Data-auth for its
                JWT-bound panels under Clerk is handled separately (WS-OWNER-A-auth). */}
            <Route path="/case/:claimId/:stage" element={<ProtectedRoute><CaseWorkspaceShell role="accountant" /></ProtectedRoute>} />
            <Route path="/case/:claimId/:stage/:panel" element={<ProtectedRoute><CaseWorkspaceShell role="accountant" /></ProtectedRoute>} />
          </>
        )}

        <Route path="/new-case-v2" element={<ProtectedRoute><React.Suspense fallback={<div className="p-6 animate-pulse">Loading...</div>}><NewCaseWizard /></React.Suspense></ProtectedRoute>} />

        {/* Role-Specific Dashboards (new architecture) */}
        {/* FL-5 5.2 — consolidated into /workdesk/data-specialist (legacy DataSpecialistDashboard retired) */}
        <Route path="/dashboard/data-specialist" element={<ProtectedRoute><DataSpecialistDashboardRedirect /></ProtectedRoute>} />
        <Route path="/dashboard/accountant" element={<ProtectedRoute><AccountantDashboardRole /></ProtectedRoute>} />
        {/* Clerk-gated P&L workbench — in-house staff access to reconciliation tools.
            Uses the same api utility (auto-Clerk token) as the rest of the staff app.
            Does NOT touch the JWT auth boundary used by the external attestation portal. */}
        <Route path="/dashboard/accountant/workbench/:reportId" element={<ProtectedRoute><StaffWorkbenchPage /></ProtectedRoute>} />
        {/* Clerk-gated Export Center — mirrors /accountant/case/:reportId/export but
            uses RoleDashboardLayout so in-house staff can reach it without JWT. */}
        <Route path="/dashboard/accountant/export/:reportId" element={<ProtectedRoute><StaffExportCenterPage /></ProtectedRoute>} />
        <Route path="/dashboard/accountant/review/:reportId" element={<ProtectedRoute><AccountantEngineReviewLanding mode="in_house_readonly" /></ProtectedRoute>} />
        {/* Methodology Comparison — surfaces the /api/v1/methodology slate
            (BI methods + coverage levers + property methods) the engine grid
            never showed. In-house only (the endpoint is Clerk/org-scoped). */}
        <Route path="/dashboard/accountant/methodology/:reportId" element={<ProtectedRoute><AccountantMethodologyView /></ProtectedRoute>} />
        {/* Analysis Tools — Monte Carlo confidence interval + driver sensitivity
            over the deterministic engines. The defend-the-number companion to the
            methodology view. In-house only (the endpoints are Clerk/org-scoped). */}
        <Route path="/dashboard/accountant/analysis/:reportId" element={<ProtectedRoute><AccountantAnalysisTools /></ProtectedRoute>} />
        {/* Anomaly Review — approve/reject anomaly resolutions + reason notes.
            Its /dashboard/anomaly-review endpoints are Clerk-gated (get_current_user),
            so this belongs to the in-house silo; the /accountant/anomalies route kept
            for back-compat is external-JWT territory where those endpoints 401. */}
        <Route path="/dashboard/accountant/anomalies/:reportId" element={<ProtectedRoute><AccountantAnomalyReviewInternal /></ProtectedRoute>} />
        <Route path="/dashboard/accountant/review/:reportId/:engineId" element={<ProtectedRoute><AccountantEngineReviewDetail mode="in_house_readonly" /></ProtectedRoute>} />
        {/* Phase 07.3 — in-house data_specialist EDIT surface. Same page,
            mode="in_house_edit" unlocks the action bar (Start review →
            Approve/Dispute/Waive) wired to /api/v1/engine-review/... POSTs.
            Convention: every Phase 07.3x retrofit reuses this /edit segment. */}
        <Route path="/dashboard/accountant/review/:reportId/:engineId/edit" element={<ProtectedRoute><AccountantEngineReviewDetail mode="in_house_edit" /></ProtectedRoute>} />

        {/* Phase 4.5 — Accountant Analysis Tab with Sub-Navigation
            4 sub-panels: Engine Review & Attest, Methodology Comparison,
            Analysis Tools / Stress-Test, Restatement Log */}
        <Route path="/reports/:reportId/analysis" element={<ProtectedRoute><AnalysisTab /></ProtectedRoute>} />

        {/* Legacy settlement path — now redirects to the attorney workspace Settlement
            stage (see SettlementStageRedirect above). The former sidebar SettlementPage
            (Phase 4.7, 13 sub-tabs) was deprecated; the live experience is
            AttorneySettlementContent → SettlementIntelligenceDashboard at
            /attorney/case/:id/settlement. Path kept so the anti-orphan route test and
            existing command-palette / bookmark entries resolve here and bounce forward. */}
        <Route path="/case/:id/settlement" element={<SettlementStageRedirect />} />

        {/* Accountant Portal Routes (Separate JWT Auth - Not Clerk).
            Declared in frontend/src/router/externalAccountantRoutes.tsx. */}
        {externalAccountantRoutes}
        
        {/* Attorney Portal Routes. PR-2 (2026-07-12) moved firm attorneys onto
            Clerk Organizations; the attorney-JWT portal is cut over. Auth
            flows through AttorneyAuthProvider (mounted at the App root,
            mirroring AccountantAuthProvider) PLUS Clerk — see
            ProtectedAttorneyRoute in AttorneyAuthContext.tsx for both paths.
            `/attorney/login` (+ reset/forgot-password) hard-redirect to the
            Clerk sign-in — no transition period, the AttorneyLogin JWT form
            is LEGACY-JWT (kept, not deleted, for the bake period). The
            token-gated package view stays OUTSIDE the guard; every other
            attorney route is wrapped in <ProtectedAttorneyRoute>. */}
        <Route path="/attorney/login" element={<AttorneyLoginRedirect />} />
        <Route path="/attorney/reset-password" element={<AttorneyLoginRedirect />} />
        <Route path="/attorney/forgot-password" element={<AttorneyLoginRedirect />} />
        {/* Phase 3.1 — Consolidated Attorney WorkDesk (replaces dashboard/portfolio/queue) */}
        <Route path="/attorney/workdesk" element={<ProtectedAttorneyRoute><AttorneyWorkDesk /></ProtectedAttorneyRoute>} />
        {/* Attorney letterhead profile — the attorney's own name/bar for demand + sworn PoL.
            Dedicated route (NOT the admin SettingsPage) since attorneys live in /attorney/*. */}
        <Route path="/attorney/letterhead" element={<ProtectedAttorneyRoute><AttorneyLetterheadPage /></ProtectedAttorneyRoute>} />
        {/* Legacy redirects — old surfaces consolidated into WorkDesk */}
        <Route path="/attorney/dashboard" element={<ProtectedAttorneyRoute><AttorneyDashboardRedirect /></ProtectedAttorneyRoute>} />
        <Route path="/attorney" element={<ProtectedAttorneyRoute><AttorneyDashboardRedirect /></ProtectedAttorneyRoute>} />
        <Route path="/attorney/portfolio" element={<ProtectedAttorneyRoute><AttorneyPortfolioRedirect /></ProtectedAttorneyRoute>} />
        <Route path="/queue/attorney" element={<ProtectedAttorneyRoute><AttorneyQueueRedirect /></ProtectedAttorneyRoute>} />
        {/* Bare case route: when CASE_WORKSPACE is on, enter the 7-stage workspace at
            Intake (retires the AttorneyCaseDetail sidebar for the case view); flag-off
            falls back to the legacy page (reversible). The former static /settlement
            redirect is removed so /settlement resolves to the workspace Settlement stage
            (a static path out-ranks the dynamic /:stage route). */}
        <Route path="/attorney/case/:caseId" element={<ProtectedAttorneyRoute>{isFeatureEnabled('CASE_WORKSPACE') ? <AttorneyCaseIndexRedirect /> : <AttorneyCaseDetail />}</ProtectedAttorneyRoute>} />
        <Route path="/attorney/case/:caseId/tools" element={<ProtectedAttorneyRoute><AdvancedToolsRedirect /></ProtectedAttorneyRoute>} />
        <Route path="/attorney/case/:caseId/documents" element={<ProtectedAttorneyRoute><CaseDocumentCenter /></ProtectedAttorneyRoute>} />

        {/* Phase 4 — Attorney Case Workspace routes (feature-flag gated) */}
        {isFeatureEnabled('CASE_WORKSPACE') && (
          <>
            <Route path="/attorney/case/:caseId/:stage" element={<ProtectedAttorneyRoute><CaseWorkspaceShell role="attorney" /></ProtectedAttorneyRoute>} />
            <Route path="/attorney/case/:caseId/:stage/:panel" element={<ProtectedAttorneyRoute><CaseWorkspaceShell role="attorney" /></ProtectedAttorneyRoute>} />
            <Route path="/adjuster/case/:caseId" element={<ProtectedRoute><RoleRoute requiredRoles={['admin','analyst','data_specialist','accountant_internal','attorney_internal']} redirectTo="/"><AdjusterCaseIndexRedirect /></RoleRoute></ProtectedRoute>} />
            <Route path="/adjuster/case/:caseId/:stage" element={<ProtectedRoute><RoleRoute requiredRoles={['admin','analyst','data_specialist','accountant_internal','attorney_internal']} redirectTo="/"><CaseWorkspaceShell role="adjuster" /></RoleRoute></ProtectedRoute>} />
            <Route path="/adjuster/case/:caseId/:stage/:panel" element={<ProtectedRoute><RoleRoute requiredRoles={['admin','analyst','data_specialist','accountant_internal','attorney_internal']} redirectTo="/"><CaseWorkspaceShell role="adjuster" /></RoleRoute></ProtectedRoute>} />
          </>
        )}
        <Route path="/attorney/case/:caseId/demand-letter" element={<ProtectedAttorneyRoute><DemandLetterGenerator /></ProtectedAttorneyRoute>} />
        <Route path="/attorney/case/:caseId/proof-of-loss" element={<ProtectedAttorneyRoute><ProofOfLossGenerator /></ProtectedAttorneyRoute>} />
        <Route path="/attorney/case/:caseId/euo-prep-memo" element={<ProtectedAttorneyRoute><EUOPrepMemoGenerator /></ProtectedAttorneyRoute>} />
        <Route path="/attorney/case/:caseId/ror-response" element={<ProtectedAttorneyRoute><RoRResponseGenerator /></ProtectedAttorneyRoute>} />
        <Route path="/attorney/case/:caseId/ror-analysis" element={<ProtectedAttorneyRoute><RoRAnalysisMemoGenerator /></ProtectedAttorneyRoute>} />
        <Route path="/attorney/case/:caseId/timeline" element={<ProtectedAttorneyRoute><BITimelinePage /></ProtectedAttorneyRoute>} />
        <Route path="/attorney/demand-letter" element={<ProtectedAttorneyRoute><DemandLetterGenerator /></ProtectedAttorneyRoute>} />
        <Route path="/attorney/proof-of-loss" element={<ProtectedAttorneyRoute><ProofOfLossGenerator /></ProtectedAttorneyRoute>} />
        <Route path="/attorney/euo-prep-memo" element={<ProtectedAttorneyRoute><EUOPrepMemoGenerator /></ProtectedAttorneyRoute>} />
        <Route path="/attorney/ror-response" element={<ProtectedAttorneyRoute><RoRResponseGenerator /></ProtectedAttorneyRoute>} />
        <Route path="/attorney/ror-analysis" element={<ProtectedAttorneyRoute><RoRAnalysisMemoGenerator /></ProtectedAttorneyRoute>} />
        <Route path="/attorney/timeline" element={<ProtectedAttorneyRoute><BITimelinePage /></ProtectedAttorneyRoute>} />

        {/* Phase 4.6 — Top-level Generators group (case-picker, no open case required) */}
        <Route path="/generators" element={<ProtectedAttorneyRoute><GeneratorsPage /></ProtectedAttorneyRoute>} />

        {/* Attorney War Room — Phase 09c: read-only deliberation view */}
        <Route path="/attorney/war-room/:reportId" element={<ProtectedAttorneyRoute><AttorneyWarRoomPage /></ProtectedAttorneyRoute>} />
        {/* Attorney Overlay viewer — Phase 09b consumer */}
        <Route path="/attorney/overlay/:reportId" element={<ProtectedAttorneyRoute><AttorneyOverlayPage /></ProtectedAttorneyRoute>} />
        {/* Attorney Package View - Public route with token (Phase 6) — stays
            outside the guard: its own URL token IS the auth. */}
        <Route path="/attorney/package/:token" element={<AttorneyPackageView />} />
        {/* Policy DNA routes removed - policy coverage now integrated into case detail page */}
        
        {/* Role-based WorkDesk routes (Phase 3.1) */}
        <Route path="/workdesk/admin" element={<ProtectedRoute><WorkDesk role="admin" /></ProtectedRoute>} />
        <Route path="/workdesk/analyst" element={<ProtectedRoute><WorkDesk role="analyst" /></ProtectedRoute>} />
        <Route path="/workdesk/data-specialist" element={<ProtectedRoute><WorkDesk role="data_specialist" /></ProtectedRoute>} />
        {/* FL-5 5.3 — DS per-case workspace (Extraction → Transactions → Validation → Handoff). */}
        <Route path="/data-specialist/case/:caseId" element={<ProtectedRoute><CaseWorkspaceShell role="data_specialist" /></ProtectedRoute>} />
        <Route path="/data-specialist/case/:caseId/:stage" element={<ProtectedRoute><CaseWorkspaceShell role="data_specialist" /></ProtectedRoute>} />
        <Route path="/workdesk/accountant-internal" element={<ProtectedRoute><InternalAccountantWorkDesk /></ProtectedRoute>} />
        {/* /workdesk/accountant-external is declared in router/externalAccountantRoutes.tsx
            (JWT tree), not here — it must NOT sit behind Clerk <ProtectedRoute>. WS2-P0. */}
        <Route path="/workdesk/attorney" element={<ProtectedAttorneyRoute><WorkDesk role="attorney" /></ProtectedAttorneyRoute>} />

        {/* Phase 4.4 — Analysis Stage with tab system. Base route is the
            discoverable feature (/analysis/:reportId, in the manifest); the
            /* splat container carries path-based tab deep-links (EXCLUDED). */}
        <Route path="/analysis/:reportId" element={<ProtectedRoute><AnalysisPage /></ProtectedRoute>} />
        <Route path="/analysis/:reportId/*" element={<ProtectedRoute><AnalysisPage /></ProtectedRoute>} />

        <Route path="/sign-out" element={<SignOutRoute />} />
        <Route path="/logout" element={<SignOutRoute />} />

        {/* 404 catch-all route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  // In test mode, skip ClerkProvider entirely
  if (isTestMode) {
    console.log('🧪 TEST MODE ENABLED - Auth bypassed');
    return (
      <GlobalErrorBoundary>
        <TestAuthProvider>
          <BrandingProvider>
            <AccountantAuthProvider>
              <AttorneyAuthProvider>
                <PermissionsProvider>
                  <AppContent />
                  <Toaster />
                </PermissionsProvider>
              </AttorneyAuthProvider>
            </AccountantAuthProvider>
          </BrandingProvider>
        </TestAuthProvider>
      </GlobalErrorBoundary>
    );
  }

  // Normal mode with Clerk
  return (
    <ClerkProvider
      publishableKey={CLERK_KEY}
      appearance={{
        elements: {
          rootBox: "mx-auto",
          card: "shadow-none"
        }
      }}
    >
      <GlobalErrorBoundary>
        <AuthTokenRegistrar>
          <BrandingProvider>
            <AccountantAuthProvider>
              <AttorneyAuthProvider>
                <PermissionsProvider>
                  <AppContent />
                  <Toaster />
                </PermissionsProvider>
              </AttorneyAuthProvider>
            </AccountantAuthProvider>
          </BrandingProvider>
        </AuthTokenRegistrar>
      </GlobalErrorBoundary>
    </ClerkProvider>
  );
}

export default App;
